import type { CollectionStrings } from "../collection-strings";

/**
 * 日本語 — collection and field labels.
 *
 * Same convention as `zh-TW.ts`: `slug`, `og:image`, `Markdown` and the
 * environment name `develop` stay in the Latin alphabet, because those are the
 * forms the team already uses and a translation would obscure rather than
 * clarify.
 */
export const ja: CollectionStrings = {
    article: {
        newArticle: {
            button: "記事を作成",
            failed:
                "記事を作成できませんでした。保存はされていません。ログイン状態を確認して、もう一度お試しください。",
        },
        retiredTags: (names) =>
            `保存できません。次のタグはアーカイブ済みです：${names.join("、")}。アーカイブ済みのタグはサイト上に一切表示されません（チップ・絞り込みリンク・ページのいずれも）。この記事から削除するか、先にタグ管理でアーカイブを解除してください。`,
        fields: {
            date: {
                name: "公開日",
                description: "形式は 2026-09-02。本日の日付が入力済みです。",
            },
            updatedAt: {
                name: "最終更新",
                description:
                    "自動で更新されます。sitemap の lastmod に使われるため、手入力すると更新日時を偽ることになります。",
            },
            slug: {
                name: "URL 用の名前 (slug)",
                description:
                    "URL に使われる部分で、両言語で共通です：/news/<slug>。記事を作成すると自動で連番が割り当てられ、後から変更できません。以前の記事は当時の slug をそのまま使い続けます。",
            },
            published: {
                name: "公開状態",
                description:
                    "初期状態はオフです。下書きは develop プレビューにのみ表示されます。",
                onLabel: "サイトに公開中",
                offLabel: "下書き — develop プレビューのみ",
            },
            tags: {
                name: "タグ",
                description: "任意。停止済みのタグは選択肢に表示されません。",
            },
            product: {
                name: "プロダクト",
                description:
                    "任意。この記事が扱うプロダクトです。会社全体のお知らせは空欄のままにしてください。",
            },
            img: {
                name: "メイン画像",
                description:
                    "任意。ファイルをアップロードするか、URL を貼り付けてください。記事ページと og:image に使われます — 一覧には画像は表示されません。",
            },
            title: { name: "タイトル" },
            description: {
                name: "概要",
                description:
                    "任意。meta description と、/news 一覧でタイトルの下に表示される 2 行に使われます。本文からの自動抽出ではなく、個別に記述してください。空欄の場合は description を出力しません。",
            },
            body: {
                name: "本文",
                description: "Markdown 形式。両言語とも必須です。",
            },
        },
    },
    tag: {
        newTag: {
            button: "タグを作成",
            failed:
                "タグを作成できませんでした。保存はされていません。ログイン状態を確認して、もう一度お試しください。",
        },
        fields: {
            date: {
                name: "作成日",
                description: "形式は 2026-09-02。本日の日付が入力済みです。",
            },
            label: {
                name: "タグ名",
                description:
                    "記事に表示されるタグの文字です。これは「コンテンツ」であり、コードの翻訳レビューは通りません。",
            },
            category: {
                name: "カテゴリータグ",
                description:
                    "このタグをニュースページの絞り込みに表示します。オフでもタグ専用ページは残り、記事にもタグとして表示されます。絞り込みの選択肢にならないだけです。",
                onLabel: "絞り込みに表示",
                offLabel: "絞り込みなし",
            },
            retired: {
                name: "停止",
                description:
                    "公開サイトからこのタグを完全に隠します：タグ表示なし、絞り込みリンクなし、ページなし。記事にはタグが残り、名前も後から復元できます。",
                onLabel: "サイトから非表示",
                offLabel: "使用中",
            },
            order: {
                name: "並び順",
                description:
                    "任意。絞り込みバーの順序を指定します。空欄の場合は作成順です。",
            },
        },
    },
    streams: {
        blog: {
            article: {
                collection: {
                    name: "ブログ",
                    singularName: "記事",
                    description:
                        "one-ai の /blog に公開される記事です。1 件ごとに専用ページを持ちます。2026-09-16 までは /news、2026-09-17 までは /notes にあった記事です（Firestore のパスは現在も notes）。",
                },
                invalidSlug: (slug) =>
                    `記事 ID「${slug}」は使用できません。小文字の英字・数字・ハイフンのみ使用してください — スペース、大文字、アンダースコアは使えません。これは永続的な URL /blog/${slug} になり、後から変更できません。`,
            },
            tag: {
                collection: {
                    name: "ブログタグ",
                    singularName: "タグ",
                    description:
                        "One AI ブログのタグです。自由に追加できます。",
                },
                invalidSlug: (slug) =>
                    `タグの slug「${slug}」は使用できません。小文字の英字・数字・ハイフンのみ使用してください — スペース、大文字、アンダースコアは使えません。これは永続的な URL /blog/tag/${slug} になり、後から変更できません。`,
            },
        },
        news: {
            article: {
                collection: {
                    name: "ニュース",
                    singularName: "記事",
                    description:
                        "one-ai の /news に公開される記事です。1 件ごとに専用ページを持ちます。2026-09-16 に作成され、記事はまだありません。",
                },
                invalidSlug: (slug) =>
                    `記事 ID「${slug}」は使用できません。小文字の英字・数字・ハイフンのみ使用してください — スペース、大文字、アンダースコアは使えません。これは永続的な URL /news/${slug} になり、後から変更できません。`,
            },
            tag: {
                collection: {
                    name: "ニュースタグ",
                    singularName: "タグ",
                    description:
                        "One AI ニュースのタグです。自由に追加できます。",
                },
                invalidSlug: (slug) =>
                    `タグの slug「${slug}」は使用できません。小文字の英字・数字・ハイフンのみ使用してください — スペース、大文字、アンダースコアは使えません。これは永続的な URL /news/tag/${slug} になり、後から変更できません。`,
            },
        },
    },
    announcements: {
        collection: {
            name: "お知らせ",
            singularName: "お知らせ",
            description: "OneLive トップページの NEWS セクションに表示されます。",
        },
        fields: {
            date: { name: "日付", description: "形式は 2026-05-22" },
            type: { name: "カテゴリ" },
            title: { name: "タイトル" },
            url: { name: "リンク" },
        },
    },
    cases: {
        collection: {
            name: "導入事例",
            singularName: "事例",
            description: "OneLive /case ページのカード一覧に表示されます。",
        },
        fields: {
            date: { name: "日付", description: "形式は 2026-06-17" },
            title: { name: "タイトル" },
            description: { name: "概要" },
            img: { name: "メイン画像の URL" },
            url: { name: "記事リンク" },
        },
    },
    tagPicker: {
        edit: "タグを選択",
        empty: "タグなし",
        remove: (name) => `${name} を外す`,
    },
    upload: {
        button: "アップロード",
        uploading: (percent) => `アップロード中… ${percent}%`,
        placeholder: "https://…",
        preview: "メイン画像のプレビュー",
        errors: {
            tooLarge: (limitMb) =>
                `画像のサイズが大きすぎます。上限は ${limitMb} MB です。`,
            notAnImage: "このファイルは画像ではありません。",
            notConfigured:
                "アップロード先が設定されていません（VITE_UPLOAD_ENDPOINT が未設定）。URL を貼り付けてください。",
            notAllowed:
                "アップロードの認証に失敗しました。一度サインアウトしてから、もう一度お試しください。",
            generic:
                "アップロードに失敗しました。もう一度試すか、URL を貼り付けてください。",
        },
    },
    richText: {
        placeholder: "本文を入力…",
        toolbar: {
            paragraph: "本文",
            heading2: "見出し",
            heading3: "小見出し",
            bold: "太字",
            italic: "斜体",
            underline: "下線",
            strikethrough: "取り消し線",
            inlineCode: "コード",
            bulletList: "箇条書き",
            numberedList: "番号付きリスト",
            quote: "引用",
            codeBlock: "コードブロック",
            link: "リンク",
            image: "画像を挿入",
            embed: "動画を挿入",
            undo: "元に戻す",
            redo: "やり直す",
        },
        link: {
            prompt: "リンク先の URL",
            invalid:
                "https:// で始まる URL、mailto: のアドレス、または / で始まるパスを入力してください。それ以外はサイトのビルド時に削除されます。",
        },
        image: {
            alt: "代替テキスト",
            altPlaceholder: "画像の内容（スクリーンリーダー用）",
            remove: "削除",
        },
        embed: {
            prompt: "YouTube または Vimeo の URL",
            unsupported:
                "埋め込めるのは YouTube と Vimeo のみです。それ以外はサイトのビルド時に削除されます。",
            remove: "削除",
        },
        upload: {
            button: "アップロード",
            uploading: (percent) => `アップロード中… ${percent}%`,
            placeholder: "https://…",
            preview: "本文画像のプレビュー",
            errors: {
                tooLarge: (limitMb) =>
                    `画像のサイズが大きすぎます。上限は ${limitMb} MB です。`,
                notAnImage: "このファイルは画像ではありません。",
                notConfigured:
                    "アップロード先が設定されていません（VITE_UPLOAD_ENDPOINT が未設定）。",
                notAllowed:
                    "アップロードの認証に失敗しました。一度サインアウトしてから、もう一度お試しください。",
                generic: "アップロードに失敗しました。もう一度お試しください。",
            },
        },
    },
    rebuild: {
        button: (environment) => `${environment} を再ビルド`,
        tooltip: (environment) =>
            `現在のデータで ${environment} サイトを再ビルドします`,
        triggered: (environments) =>
            `${environments} を再ビルドしています。ビルドが完了すると変更が反映されます（通常 1〜2 分）。`,
        allFailed: (environments) =>
            `${environments} の再ビルドを開始できませんでした。データは保存されています。再ビルドのみ失敗したので、「再ビルド」ボタンからやり直してください。`,
        partial: (ok, failed) =>
            `保存しました。${ok} は再ビルド中ですが、${failed} の再ビルドを開始できませんでした。「再ビルド」ボタンからやり直してください。`,
    },
};
